import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { Navigate, Route, Routes } from "react-router-dom";
import { LocaleProvider } from "antd";
import { IntlProvider } from "react-intl";

import AppLocale from "lngProvider";
import MainApp from "./MainApp";
import SignIn from "../SignIn";
import Terms from "../Terms";
import UserRoleSelection from "../UserRoleSelection";
import UserSiteSelection from "../UserSiteSelection";
import VerifyAccount from "../VerifyAccount";
import ForgotPassword from "../ForgotPassword";
import GoogleAuthenticate from "../GoogleAuthenticate";

import VerifyAuthenticatorCode from "../VerifyAuthenticatorCode";
import CircularProgress from "../../components/CircularProgress";

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK,
} from "../../constants/ThemeSetting";

import {
  bootstrapAuth,
} from "appRedux/actions/Auth";
import {
  onLayoutTypeChange,
  onNavStyleChange,
  setThemeType,
} from "appRedux/actions/Setting";

const getRouteRole = (pathname, authUser) => {
  const routeRoleMap = {
    "/admin": "ADMIN",
    "/mrt": "MRT",
    "/receptionist": "RECEPTIONIST",
  };
  const normalizedPathname = pathname.toLowerCase();
  const routeRole = Object.keys(routeRoleMap).find((path) => normalizedPathname.startsWith(path));
  const role = routeRole ? routeRoleMap[routeRole] : null;
  const userRoles = authUser?.roles || [];

  if (!role || !authUser) {
    return null;
  }

  if (!userRoles.length) {
    return role;
  }

  const hasRouteRole = userRoles.some((userRole) => {
    const roleName = typeof userRole === "string" ? userRole : userRole?.name || userRole?.role || userRole?.authority;
    return roleName?.toUpperCase() === role;
  });

  return hasRouteRole || role === "RECEPTIONIST" ? role : null;
};

const RestrictedRoute = ({ children, authenticated, role, site, initialized, loading }) => {
  if (!initialized || loading) {
    return <CircularProgress />;
  }

  if (authenticated && !site) {
    return <Navigate to="/user-site-selection" replace />;
  }

  if (authenticated && !role) {
    return <Navigate to="/user-role-selection" replace />;
  }

  if (authenticated && role) {
    return children;
  } else {
    return <Navigate to="/signin" replace />;
  }
};

function App({
  locale,
  navStyle,
  themeType,
  layoutType,
  authUser,
  initURL,
  appLoading,
  initialized,
  loading,
  bootstrapAuth,
  setThemeType,
  onNavStyleChange,
  onLayoutTypeChange,
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const setLayoutTypeFn = (layoutType) => {
    document.body.classList.remove(
      "boxed-layout",
      "framed-layout",
      "full-layout"
    );
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.add("full-layout");
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.add("boxed-layout");
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.add("framed-layout");
    }
  };

  const setNavStyleFn = (navStyle) => {
    if (
      navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add("full-scroll", "horizontal-layout");
    } else {
      document.body.classList.remove("full-scroll", "horizontal-layout");
    }
  };

  useEffect(() => {
    bootstrapAuth();

    if (searchParams.has("theme") || localStorage?.getItem("THEME_TYPE")) {      
      const themeType = searchParams.get("theme") ? searchParams.get("theme") :  localStorage?.getItem("THEME_TYPE");
      setThemeType(themeType);
    }
    if (searchParams.has("nav-style")) {
      onNavStyleChange(searchParams.get("nav-style"));
    }
    if (searchParams.has("layout-type")) {
      onLayoutTypeChange(searchParams.get("layout-type"));
    }
  }, []);

  useEffect(() => {
    setLayoutTypeFn(layoutType);
    setNavStyleFn(navStyle);
  }, [layoutType, navStyle]);

  useEffect(() => {
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.remove("dark-theme");
    }
  }, [themeType]);

  if (appLoading) {
    return <CircularProgress />;
  }

  if (location.pathname === "/") {
    if (!authUser) {
      return <Navigate to="/signin" />;
    } else if (initURL === "" || initURL === "/" || initURL === "/signin") {
      if (authUser?.authenticated) {
        if (authUser.site) {
          if (authUser.role) {
            if (authUser.role === "ADMIN") {
              return <Navigate to="/admin/dashboard" />;
            } else if (authUser.role === "MRT") {
              return <Navigate to="/mrt/worklist" />;
            } else if (authUser.role === "RECEPTIONIST") {
              return <Navigate to="/receptionist/dashboard" />;
            }
          } else {
            return <Navigate to="/user-role-selection" />;
          }
        } else {
          return <Navigate to="/user-site-selection" />;
        }
      } else if (!authUser.isAuthenticator) {
        return <Navigate to="/create-authenticator-account" replace />;
      } else {
        return <Navigate to="/verify-authenticator-code" replace />;
      }
    } else {
      return <Navigate to={initURL} />;
    }
  }

  const currentAppLocale = AppLocale[locale.locale];
  const effectiveRole = authUser?.role || getRouteRole(location.pathname, authUser);

  return (
    <LocaleProvider locale={currentAppLocale.antd}>
      <IntlProvider
        locale={currentAppLocale.locale}
        messages={currentAppLocale.messages}
      >
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-account" element={<VerifyAccount />} />
          <Route
            path="/create-authenticator-account"
            element={<GoogleAuthenticate />}
          />
          <Route
            path="/verify-authenticator-code"
            element={<VerifyAuthenticatorCode />}
          />
          <Route path="/user-site-selection" element={<UserSiteSelection />} />
          <Route path="/user-role-selection" element={<UserRoleSelection />} />
          {/* <Route path="/signup" element={<SignUp />} /> */}
          <Route path="/terms" element={<Terms />} />
          <Route
            path="/*"
            element={
              <RestrictedRoute
                authenticated={authUser?.authenticated || false}
                role={effectiveRole}
                site={authUser?.site || null}
                initialized={initialized}
                loading={loading || appLoading}
              >
                <MainApp routeRole={effectiveRole} />
              </RestrictedRoute>
            }
          />
        </Routes>
      </IntlProvider>
    </LocaleProvider>
  );
}

const mapStateToProps = ({ settings, auth }) => {
  const { locale, navStyle, themeType, layoutType } = settings;
  const { authUser, initURL, appLoading, initialized, loading } = auth;
  return {
    locale,
    navStyle,
    themeType,
    layoutType,
    authUser,
    initURL,
    appLoading,
    initialized,
    loading,
  };
};

export default connect(mapStateToProps, {
  bootstrapAuth,
  setThemeType,
  onNavStyleChange,
  onLayoutTypeChange,
})(App);
